import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import InputField from '../inputField';
import ActionButton from '../actionButton';
import CustomRichTextEditor from '../customRichTextEditor';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ spacing }) => ({
  rootRichText: {
    width: '100%',
    position: 'relative',
  },
  mainRichText: {
    width: '100%',
  },
  textEditor: {
    '& .ql-toolbar': {
      borderTopLeftRadius: 3,
      borderTopRightRadius: 3,
    },
    '& .ql-container': {
      height: 188,
      borderBottomLeftRadius: 3,
      borderBottomRightRadius: 3,
      '& .ql-blank::before': {
        lineHeight: '24px',
      },
    },
  },
  inputText: {
    '& > textarea': {
      paddingRight: spacing(12),
    },
  },
  inputTextEdit: {
    '& > textarea': {
      paddingRight: spacing(18),
    },
  },
  actionButtons: {
    display: 'grid',
    gridTemplateColumns: 'repeat(1, 30px)',
    gridColumnGap: 2,
    width: 'fit-content',
    position: 'absolute',
    top: 4,
    right: 16,
  },
  actionButtonsRichText: {
    top: 6,
    right: 6,
  },
  actionButtonsEdit: {
    gridTemplateColumns: 'repeat(2, 30px)',
  },
  error: {
    position: 'absolute',
    left: 0,
    bottom: -20,
  },
  submitIcon: {
    width: 16,
    height: 16,
  },
}));

const SubmittableInput = ({
  className = '',
  label = '',
  placeholder = '',
  value,
  errorMessage = '',
  isEdit = false,
  hasError = false,
  isSendDisabled = false,
  isRichText = false,
  isFocused = false,
  isFocusedAdd = false,
  handleFocus = () => {},
  onChange,
  onSubmit,
  onCancel = () => {},
}) => {
  const classes = useStyles();

  const renderSubmit = () => (
    <div
      className={classNames(classes.actionButtons, {
        [classes.actionButtonsEdit]: isEdit || isFocused,
        [classes.actionButtonsRichText]: isRichText,
      })}
    >
      <ActionButton
        iconClass={classes.submitIcon}
        type={ACTION_BUTTON_TYPES.SEND}
        isDisabled={isSendDisabled || hasError}
        onClick={() => {
          handleFocus();
          onSubmit();
        }}
      />
      {(isEdit || isFocused) && (
        <ActionButton
          type={ACTION_BUTTON_TYPES.CLOSE}
          onClickHandler={() => {
            handleFocus();
            onCancel();
          }}
        />
      )}
    </div>
  );

  return isRichText && (isFocused || isFocusedAdd) ? (
    <div className={classes.rootRichText}>
      <CustomRichTextEditor
        className={classes.mainRichText}
        customEditorClass={classes.textEditor}
        customErrorClass={classes.error}
        label={label}
        placeholder={placeholder}
        value={value}
        hasError={hasError}
        errorMessage={errorMessage}
        onUpdateText={val => onChange(val)}
      />
      {renderSubmit()}
    </div>
  ) : (
    <InputField
      className={classNames(
        classes.inputText,
        { [classes.inputTextEdit]: isEdit },
        className
      )}
      customErrorClass={classes.error}
      label={label}
      placeholder={placeholder}
      error={hasError}
      errorMessage={errorMessage}
      value={value}
      maxRows={5}
      onChange={e => onChange(e.target.value)}
      endAdornment={renderSubmit()}
      fullWidth
      multiline
    />
  );
};

SubmittableInput.propTypes = {
  className: PropTypes.string,
  isRichText: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isEdit: PropTypes.bool,
  isSendDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isFocusedAdd: PropTypes.bool,
  handleFocus: PropTypes.func,
};

export default SubmittableInput;
